import { MathUtils } from 'three';
import { PLAYER_SCREEN_X, VIEW_HEIGHT, VIEW_WIDTH } from '../data/tuning';

/** Pure numbers of the level scene (GDD §14.1, §5.2), apart so they can be tested without WebGL. */

/** Vertical field of view of the fixed side camera. */
export const FOV_DEGREES = 35;

/** Distance at which a camera with this fov sees `height` u at z = 0: d = (height / 2) / tan(fov / 2). */
export const cameraDistanceFor = (fovDegrees: number, height: number): number =>
  height / 2 / Math.tan(MathUtils.degToRad(fovDegrees / 2));

/** Distance that shows exactly 12 u of height at z = 0: d = 6 / tan(17.5 degrees) = 19.03 u. */
export const CAMERA_DISTANCE = cameraDistanceFor(FOV_DEGREES, VIEW_HEIGHT);

/** The logical view is always 16:9 (GDD §5.2); other windows get bands. */
export const VIEW_ASPECT = 16 / 9;

export interface ViewRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The largest 16:9 rectangle that fits the container, centred. A zero-sized container gives an empty one. */
export function fitView(containerWidth: number, containerHeight: number): ViewRect {
  const width = Math.max(0, Math.min(containerWidth, containerHeight * VIEW_ASPECT));
  if (width === 0) return { x: 0, y: 0, width: 0, height: 0 };
  const height = width / VIEW_ASPECT;
  return { x: (containerWidth - width) / 2, y: (containerHeight - height) / 2, width, height };
}

/** x of the camera for a plane at `playerX`, so that the plane sits at 30 % of the view width (§5.2). */
export const cameraXFor = (playerX: number): number => playerX + (0.5 - PLAYER_SCREEN_X) * VIEW_WIDTH;

/** Linear interpolation between the previous and the current tick; alpha in [0, 1] (GDD §15.4). */
export const lerp = (previous: number, current: number, alpha: number): number =>
  previous + (current - previous) * alpha;

export const toRadians = (degrees: number): number => MathUtils.degToRad(degrees);
export const toDegrees = (radians: number): number => MathUtils.radToDeg(radians);

/** Height in world units that a camera at `distance` with this fov sees at z = 0. */
export const visibleHeightAt = (distance: number, fovDegrees: number): number =>
  2 * distance * Math.tan(MathUtils.degToRad(fovDegrees / 2));

/** Share of the parallax that stays with Reduced motion on (GDD §13.6): the background moves at half. */
export const REDUCED_PARALLAX = 0.5;
